import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import DiagnosticCta from "@/components/DiagnosticCta";
import ChecklistGate from "@/components/ChecklistGate";
import FaqBlock from "@/components/FaqBlock";
import Seo from "@/components/Seo";
import { SEGMENTS, GCC_FAQS, AI_NATIVE_FAQS, FOUNDER } from "@/data/revamp";
import { faqSchema, breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const SEO_BY_SLUG = {
  "gcc-support": {
    title: "GCC Support — Launch and scale your India capability centre",
    description:
      "Orphion builds the people function for Global Capability Centres in India: entity-readiness, first 50 hires, GCC leadership hiring and ongoing HR operations. GCC Launch Sprint — hire-ready in 90 days, fixed fee.",
    faqs: GCC_FAQS,
    name: "GCC Support",
  },
  "ai-native-it-services": {
    title: "AI-Native IT Services — Scale the team without the culture drift",
    description:
      "HR for AI-native IT services and product companies (50–1,500 people): fast calibrated hiring with AI-run sourcing and screening, HR structure, and Fractional CHRO leadership. AI in our delivery, AI advisory for yours.",
    faqs: AI_NATIVE_FAQS,
    name: "AI-Native IT Services",
  },
};

export default function SegmentPage({ slug }) {
  const seg = SEGMENTS.find((s) => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!seg) return <Navigate to="/" replace />;

  const seo = SEO_BY_SLUG[slug];

  return (
    <div className="bg-[var(--bg)]">
      <Seo
        title={seo.title}
        description={seo.description}
        path={`/${slug}`}
        jsonLd={[
          faqSchema(seo.faqs),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: seo.name, path: `/${slug}` },
          ]),
        ]}
      />

      <PageHero
        overline={seg.overline}
        titleA={seg.heroA}
        titleAccent={seg.heroAccent}
        titleB={seg.heroB}
        intro={seg.heroIntro}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="mt-10"
        >
          <Link to="/book-diagnostic" className="btn-pill" data-testid="segment-hero-cta">
            <span>Book a free diagnostic call</span>
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
          </Link>
        </motion.div>
      </PageHero>

      {/* Pain stages */}
      <section className="py-16 md:py-24 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline mb-4">{seg.painTitle}</p>
          <p className="text-[var(--ink-70)] text-sm md:text-base max-w-xl mb-12">{seg.painIntro}</p>
          <div className="grid md:grid-cols-2 gap-6">
            {seg.pains.map((p, i) => (
              <motion.div
                key={p.stage}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: 0.06 * i, ease: EASE }}
                className="border border-[var(--line-strong)] p-7 md:p-8 bg-[var(--bg)] hover:bg-[var(--bg-2)] transition-colors"
              >
                <span className="font-serif-accent italic text-[var(--accent)] text-lg block mb-3">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-xl md:text-2xl tracking-tight text-[var(--ink)] mb-3">
                  {p.stage}
                </h3>
                <p className="text-sm text-[var(--ink-70)] leading-relaxed mb-5">{p.problem}</p>
                <Link
                  to={`/services/${p.serviceSlug}`}
                  className="link-slide text-xs uppercase tracking-[0.22em] text-[var(--accent)]"
                >
                  {p.service} →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Flagship offer */}
      <section className="py-16 md:py-24 border-b border-[var(--line-strong)] bg-[var(--bg-2)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <p className="overline mb-4">The offer</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] leading-[1.0] text-[var(--ink)] mb-6">
              {seg.offerTitle}
            </h2>
            <p className="text-[var(--ink-80)] leading-relaxed mb-6">{seg.offerBody}</p>
            <p className="text-xs uppercase tracking-[0.24em] text-[var(--accent)]">{seg.offerNote}</p>
          </div>
          <div className="md:col-span-6">
            <ul className="space-y-5">
              {seg.offerPoints.map((pt, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.08 * i, ease: EASE }}
                  className="border-l-2 border-[var(--accent)] pl-5 text-sm md:text-base text-[var(--ink-80)] leading-relaxed"
                >
                  {pt}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Who you'll work with */}
      <section className="py-16 md:py-20 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4">
            <p className="overline mb-4">{seg.whoTitle}</p>
            <p className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)]">
              {FOUNDER.name}
            </p>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--ink-50)] mt-2">
              {FOUNDER.credentials.join(" · ")}
            </p>
          </div>
          <div className="md:col-span-8">
            <p className="text-[var(--ink-80)] leading-relaxed max-w-2xl">{seg.whoBody}</p>
          </div>
        </div>
      </section>

      <FaqBlock
        kicker="FAQ"
        title={
          <>
            The questions every{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              {slug === "gcc-support" ? "GCC leader" : "founder"}
            </span>{" "}
            asks first.
          </>
        }
        items={seo.faqs}
      />

      {slug === "gcc-support" && <ChecklistGate />}

      <DiagnosticCta />
    </div>
  );
}
